import {
  AssignationMode,
  BillingMode,
  DisplayMode,
  InvitationApprovalMode,
  InvitationMode,
  BackendCompatibleLocales,
  PositionsDistributionMode,
  SeatDistributionModeLabels,
  WaitingListAcceptFormat,
  WLAccessModeEnum,
} from '../../enums';
import { string, z } from 'zod';
import { canvasImageOrUrlSchema } from '../shared/canvasImage.schema';
import dayjs, { Dayjs } from 'dayjs';

export const CreateWishListSettingsFormValidatorSchema = z
  .object({
    /**
     * Display-only fields. They have to be declared so the resolver's input
     * type covers the step's form type, but the `.transform` at the end of this
     * schema drops them again, so they never reach the request body — a raw
     * HTMLCanvasElement would otherwise serialise as `{}` over the stored URL.
     */
    coverImage: z.string().nullable(),
    waitingListImageUrl: canvasImageOrUrlSchema,
    totalSeatPrice: z.number().optional(),
    experienceName: z.record(z.string().min(1), z.string()),
    name: z.string().min(1),
    highlighted: z.boolean(),

    billingVariables: z.object({
      distributionRate: z.string(),
      maxDistributionFee: z.string(),
      minDistributionFee: z.string(),
      vatRate: z.string(),
    }),

    description: z.record(z.string().min(1), z.string()),
    keywords: z.record(z.string().min(1), z.string().array()),
    billingMode: z.enum(BillingMode),
    accessMode: z.enum(WLAccessModeEnum),
    price: z.string(),
    originalPrice: z.union([z.string(), z.number()]),
    showOriginalValue: z.boolean(),
    directSales: z.boolean(),
    prepaidFee: z.boolean(),
    termsAndConditionFileURL: string(),
    positionsDistributionMode: z.string(),
    displayMode: z.enum(DisplayMode),
    minNumberOfSeatsPerPosition: z.number(),
    maxNumberOfSeatsPerPosition: z.number(),
    /**
     * Both durations are seeded by getInitialValues, but DurationInputWithToggle
     * writes null when its number input is cleared. Requiring them would make
     * Save a silent no-op — handleSubmit never fires and neither field renders
     * an error — so null has to validate here and is normalised at submit.
     */
    voucherExpirationTimeOutInMs: z.number().nullable(),
    rsvpReminderTimeOutInMs: z.number().nullable(),
    fixedNumberOfSeatsPerPosition: z.number(),
    participationEndDate: z
      .instanceof(dayjs as unknown as typeof Dayjs)
      .nullable(),
    /**
     * `InvitationParameters` is a Partial on the entity, so a waiting list can
     * come back missing any of these. The defaults below are exactly the
     * fallback object getInitialValues uses when there is no waiting list, so
     * form state may be partial while the validated output is always complete.
     */
    invitationParameters: z
      .object({
        approvalMode: z
          .enum(InvitationApprovalMode)
          .default(InvitationApprovalMode.DEFAULT),
        approvalRules: z
          .array(
            z.object({
              description: z.string(),
              expression: z.string(),
              name: z.string(),
            })
          )
          .optional(),
        minNumberOfInvitations: z.number().default(1),
        maxNumberOfInvitations: z.number().default(10),
        assignationMode: z
          .enum(AssignationMode)
          .default(AssignationMode.HOST_RSVP),
        invitationMode: z.enum(InvitationMode).optional(),
        specialGuestEnabled: z.boolean().default(false),
      })
      .refine(
        (data) => {
          return data.minNumberOfInvitations <= data.maxNumberOfInvitations;
        },
        {
          message: 'wl_settings_max_tickets_per_host_validation', // wl_settings_max_tickets_per_host_validation
          path: ['maxNumberOfInvitations'], // path of error
        }
      )
      .nullable(),
  })
  .superRefine((schema, ctx) => {
    const {
      positionsDistributionMode,
      participationEndDate,
      minNumberOfSeatsPerPosition,
      maxNumberOfSeatsPerPosition,
    } = schema;

    const isRandomDistributionMode =
      positionsDistributionMode === PositionsDistributionMode.RANDOM ||
      positionsDistributionMode === PositionsDistributionMode.WEIGHTED_RANDOM;

    // Random-based modes need a participation end date; every mode now uses the same
    // [min, max] seat range (random derives fixedNumberOfSeatsPerPosition = max at submit).
    if (isRandomDistributionMode && !participationEndDate) {
      ctx.addIssue({
        message: 'ADD DATE',
        code: z.ZodIssueCode.custom,
        path: ['participationEndDate'],
      });
    }
    if (!minNumberOfSeatsPerPosition) {
      ctx.addIssue({
        message: 'Invalid input',
        code: z.ZodIssueCode.invalid_type,
        expected: 'number',
        received: 'null',
        path: ['minNumberOfSeatsPerPosition'],
      });
    }
    if (!maxNumberOfSeatsPerPosition) {
      ctx.addIssue({
        message: 'Invalid input',
        code: z.ZodIssueCode.invalid_type,
        expected: 'number',
        received: 'null',
        path: ['maxNumberOfSeatsPerPosition'],
      });
    }

    if (
      minNumberOfSeatsPerPosition &&
      maxNumberOfSeatsPerPosition &&
      minNumberOfSeatsPerPosition > maxNumberOfSeatsPerPosition
    ) {
      ctx.addIssue({
        message: 'Invalid input',
        code: z.ZodIssueCode.custom,
        path: ['maxNumberOfSeatsPerPosition'],
      });
    }
  })
  .superRefine((schema, ctx) => {
    const reminder = schema.rsvpReminderTimeOutInMs ?? 0;
    const expiration = schema.voucherExpirationTimeOutInMs ?? 0;

    if (reminder > 0 && reminder >= expiration) {
      ctx.addIssue({
        message: 'rsvp_reminder_before_expiration_validation',
        code: z.ZodIssueCode.custom,
        path: ['rsvpReminderTimeOutInMs'],
      });
    }
  })
  .superRefine((schema, ctx) => {
    const { positionsDistributionMode, displayMode } = schema;
    const randomDistributionIncompatibleDisplayModes = [
      DisplayMode.DETAILED,
      DisplayMode.DETAILED_LIMITED,
      DisplayMode.INFORMATIVE,
    ];
    const parkingDistributionIncompatibleDisplayModes = [
      DisplayMode.CONTEST,
      DisplayMode.MERCHANDISE,
      DisplayMode.INFORMATIVE,
    ];
    if (
      (positionsDistributionMode === PositionsDistributionMode.RANDOM &&
        randomDistributionIncompatibleDisplayModes.includes(
          displayMode as DisplayMode
        )) ||
      (positionsDistributionMode === PositionsDistributionMode.WITH_PARKING &&
        parkingDistributionIncompatibleDisplayModes.includes(
          displayMode as DisplayMode
        ))
    ) {
      ctx.addIssue({
        message:
          positionsDistributionMode === PositionsDistributionMode.RANDOM
            ? 'Display mode DETAILED, INFORMATIVE not allowed for selected distribution mode'
            : 'Display mode  CONTEST, MERCHANDISE, INFORMATIVE not allowed for selected distribution mode',
        code: z.ZodIssueCode.custom,
        path: ['displayMode'],
      });
    }
  })
  .transform(
    ({
      coverImage: _coverImage,
      waitingListImageUrl: _waitingListImageUrl,
      totalSeatPrice: _totalSeatPrice,
      ...submittable
    }) => submittable
  );
const buildTicketPoolIdsIssue = (input: string[] | undefined) => ({
  code: 'too_small' as const,
  minimum: 1,
  origin: 'array' as const,
  inclusive: true,
  input,
  path: ['ticketPoolIds'],
  message: 'general_label_required',
});

const voucherTextIssue = {
  code: z.ZodIssueCode.custom,
  path: ['voucherText'],
  message: 'event_error_no-default-translation',
};

export const CreateWishListTicketsFormValidatorSchema = z
  .object({
    voucherText: z
      .partialRecord(z.enum(BackendCompatibleLocales), z.string())
      .optional(),
    ticketsToAdd: z.number(),
    downloadFormat: z.enum(WaitingListAcceptFormat),
    estimatedTickets: z.number(),
    /**
     * Unset for a waiting list that has no mode yet. The submit handler maps
     * anything that is not TICKET to VOUCHER, so the same default is applied
     * here: `z.input` stays optional, the validated output is always set.
     */
    seatDistributionMode: z
      .enum(SeatDistributionModeLabels)
      .default(SeatDistributionModeLabels.VOUCHER),
    ticketPoolIds: z.string().array(),
    secondaryTicketPoolIds: z.string().array(),
    /** Preview image for the voucher; always seeded ('' when unset). */
    voucherImageUrl: z.string(),
  })
  .superRefine((schema, ctx) => {
    const { seatDistributionMode, ticketPoolIds } = schema;
    if (seatDistributionMode === SeatDistributionModeLabels.TICKET) {
      if (ticketPoolIds?.length === 0) {
        ctx.addIssue(buildTicketPoolIdsIssue(ticketPoolIds));
      }
    } else {
      if (
        seatDistributionMode === SeatDistributionModeLabels.VOUCHER &&
        (!schema.voucherText || !schema?.voucherText?.en) &&
        schema.ticketsToAdd > 0
      )
        ctx.addIssue(voucherTextIssue);
    }
  });

/**
 * What the settings step submits, as opposed to what it edits. Display-only
 * fields (the cropped canvas, the cover image, the computed total) live in the
 * form type but not in this schema, so zodResolver strips them before submit.
 */
export type CreateWishListSettingsFormValidator = z.output<
  typeof CreateWishListSettingsFormValidatorSchema
>;

export const EditWishListTicketsFormValidatorSchema = z
  .object({
    voucherText: z
      .partialRecord(z.enum(BackendCompatibleLocales), z.string())
      .optional(),
    ticketsToAdd: z.number(),
    downloadFormat: z.enum(WaitingListAcceptFormat),
    estimatedTickets: z.number(),
    /** Same three options the step renders in edit mode; see the create schema. */
    seatDistributionMode: z
      .enum(SeatDistributionModeLabels)
      .default(SeatDistributionModeLabels.VOUCHER),
    ticketPoolIds: z.string().array(),
    secondaryTicketPoolIds: z.string().array(),
    /** Preview image for the voucher; always seeded ('' when unset). */
    voucherImageUrl: z.string(),
  })
  .superRefine((schema, ctx) => {
    const { seatDistributionMode, ticketPoolIds } = schema;
    if (seatDistributionMode === SeatDistributionModeLabels.TICKET) {
      if (ticketPoolIds?.length === 0) {
        ctx.addIssue(buildTicketPoolIdsIssue(ticketPoolIds));
      }
    } else {
      if (
        seatDistributionMode === SeatDistributionModeLabels.VOUCHER &&
        (!schema.voucherText || !schema?.voucherText?.en) &&
        schema.ticketsToAdd > 0
      ) {
        ctx.addIssue(voucherTextIssue);
      }
    }
  });

/** Validated output of the tickets step (distribution mode always set). */
export type EditWishListTicketsFormValidator = z.infer<
  typeof EditWishListTicketsFormValidatorSchema
>;

export const FGOWishListsValidatorSchema = z.object({
  eventStartDate: z.string().optional(),
  eventEndDate: z.string().optional(),
  mustHaveSeatsWaiting: z.boolean().optional(),
  /** Excludes waiting lists the FGO marked complete. */
  excludeCompleted: z.boolean().optional(),
  includeClosed: z.boolean(),
  includeOpen: z.boolean(),
  // Omitted by callers that want the backend's default for these buckets.
  includeSoon: z.boolean().optional(),
  includeDraft: z.boolean().optional(),
  /** Matches the wish list's own *name*, not its category — use `ticketCategories` for that. */
  categoryPart: z.string().optional(),
  experienceNamePart: z.string().optional(),
  /** Original misspelling of `experienceNamePart`, still accepted by the API. */
  exprerienceNamePart: z.string().optional(),
  /** Exact `filters.ticketCategory` codes, sent verbatim — never trimmed or case-folded. */
  ticketCategories: z.array(z.string()).optional(),
  /** Includes wish lists with no ticket category: absent, null or empty. A flag, not a magic code. */
  includeUncategorised: z.boolean().optional(),
  /** Session groups to match. */
  experienceGroupIds: z.array(z.string()).optional(),
  /** Includes experiences whose session is in no group. A flag, like `includeUncategorised`. */
  includeUngrouped: z.boolean().optional(),
});

export type FGOWishListsValidator = z.infer<typeof FGOWishListsValidatorSchema>;
export type CreateWishListsValidator = z.infer<
  typeof CreateWishListSettingsFormValidatorSchema
>;
